// Walk a parsed SCAD Program and produce structured outline data — render
// tree, user-defined modules, and user-defined functions — each carrying
// source offsets so the UI can do click-to-jump and click-to-3D-select.

import type {
  Program,
  Statement,
  ModuleCallStatement,
  ModuleDefinitionStatement,
  FunctionDefinitionStatement,
  ForStatement,
  IfStatement,
  Expression,
} from '../scad/ast';

export interface RenderNode {
  name: string;         // 'cube', 'translate', 'difference', user-module name, 'for', 'if'
  summary: string;      // short human arg summary e.g. '[20,20,20]' or 'h=10, r=5'
  from: number;         // source offset — click to jump
  to: number;
  line: number;         // 1-based
  children: RenderNode[];
}

export interface SymbolEntry {
  name: string;
  signature: string;    // '(w, d, h)' for modules/fns; '' for plain names
  from: number;
  line: number;
}

export interface OutlineData {
  renderTree: RenderNode[];
  modules: SymbolEntry[];
  functions: SymbolEntry[];
}

const MAX_SUMMARY = 48;

function trim(s: string, n: number = MAX_SUMMARY): string {
  return s.length <= n ? s : s.slice(0, n - 1) + '…';
}

export function stringifyExpression(expr: Expression): string {
  switch (expr.type) {
    case 'NumberLiteral':      return String(expr.value);
    case 'BooleanLiteral':     return expr.value ? 'true' : 'false';
    case 'StringLiteral':      return JSON.stringify(expr.value);
    case 'IdentifierExpression': return expr.name;
    case 'VectorExpression': {
      const parts = expr.elements.map(stringifyExpression);
      return '[' + parts.join(', ') + ']';
    }
    case 'UnaryExpression':    return expr.operator + stringifyExpression(expr.operand);
    case 'BinaryExpression':   return stringifyExpression(expr.left) + expr.operator + stringifyExpression(expr.right);
    case 'FunctionCallExpression': {
      const args = (expr.args || []).map(a => (a.name ? a.name + '=' : '') + stringifyExpression(a.value));
      return expr.name + '(' + args.join(', ') + ')';
    }
    case 'RangeExpression': {
      const s = stringifyExpression(expr.start);
      const e = stringifyExpression(expr.end);
      if (expr.step) return `[${s}:${stringifyExpression(expr.step)}:${e}]`;
      return `[${s}:${e}]`;
    }
    default:
      return '…';
  }
}

function summarizeArgs(args: ModuleCallStatement['args']): string {
  if (!args || args.length === 0) return '';
  const parts = args.map(a => {
    const v = stringifyExpression(a.value);
    return a.name ? `${a.name}=${v}` : v;
  });
  return trim(parts.join(', '));
}

function summarizeModuleParams(params: ModuleDefinitionStatement['params']): string {
  if (!params || params.length === 0) return '()';
  const parts = params.map(p => (p.defaultValue ? `${p.name}=${stringifyExpression(p.defaultValue)}` : p.name));
  return '(' + trim(parts.join(', '), 40) + ')';
}

function buildRenderNode(stmt: Statement): RenderNode | null {
  if (stmt.type === 'ModuleCallStatement') {
    const m = stmt as ModuleCallStatement;
    return {
      name: m.name,
      summary: summarizeArgs(m.args),
      from: m.from.offset,
      to: m.to.offset,
      line: m.from.line,
      children: (m.children || []).map(buildRenderNode).filter((x): x is RenderNode => !!x),
    };
  }
  if (stmt.type === 'ForStatement') {
    const f = stmt as ForStatement;
    return {
      name: 'for',
      summary: `${f.variable} = ${stringifyExpression(f.range)}`,
      from: f.from.offset,
      to: f.to.offset,
      line: f.from.line,
      children: (f.body || []).map(buildRenderNode).filter((x): x is RenderNode => !!x),
    };
  }
  if (stmt.type === 'IfStatement') {
    const i = stmt as IfStatement;
    const kids: RenderNode[] = [];
    for (const s of i.thenBody || []) { const n = buildRenderNode(s); if (n) kids.push(n); }
    if (i.elseBody && i.elseBody.length) {
      const elseKids: RenderNode[] = [];
      for (const s of i.elseBody) { const n = buildRenderNode(s); if (n) elseKids.push(n); }
      if (elseKids.length) {
        kids.push({
          name: 'else',
          summary: '',
          from: i.elseBody[0].from.offset,
          to: i.elseBody[i.elseBody.length - 1].to.offset,
          line: i.elseBody[0].from.line,
          children: elseKids,
        });
      }
    }
    return {
      name: 'if',
      summary: trim(stringifyExpression(i.condition), 40),
      from: i.from.offset,
      to: i.to.offset,
      line: i.from.line,
      children: kids,
    };
  }
  // Skip module/function definitions and plain assignments from the render tree
  return null;
}

export function buildOutline(program: Program): OutlineData {
  const data: OutlineData = { renderTree: [], modules: [], functions: [] };

  for (const stmt of program.statements) {
    if (stmt.type === 'ModuleDefinitionStatement') {
      const m = stmt as ModuleDefinitionStatement;
      data.modules.push({
        name: m.name,
        signature: summarizeModuleParams(m.params),
        from: m.from.offset,
        line: m.from.line,
      });
      continue;
    }
    if (stmt.type === 'FunctionDefinitionStatement') {
      const f = stmt as FunctionDefinitionStatement;
      data.functions.push({
        name: f.name,
        signature: summarizeModuleParams(f.params),
        from: f.from.offset,
        line: f.from.line,
      });
      continue;
    }
    const node = buildRenderNode(stmt);
    if (node) data.renderTree.push(node);
  }

  // Sort symbols by line for a stable top-to-bottom view
  data.modules.sort((a, b) => a.line - b.line);
  data.functions.sort((a, b) => a.line - b.line);

  return data;
}
